<template>
  <div v-if="performanceTable" class="flex flex-col items-start gap-6">
    <router-link
      id="back-to-list-link"
      @click="performanceReservationStore.setSelectedTableIds([])"
      class="text-fg-link-primary flex items-center gap-2 text-sm"
      :to="{ name: PERFORMANCE_ROUTE_NAMES.TABLES }"
      replace
    >
      <ResArrowLeftIcon />
      <span>Back to Table List</span>
    </router-link>

    <SlotPanel classes="w-full">
      <PerformanceTableForm
        :key="performanceTable.id"
        :initialTable="initialPerformanceTable"
        v-model="performanceTable"
        :hideStandingRoomSwitch="typeof tableId !== 'string'"
      >
        <template #actions>
          <component
            v-if="allowToDelete"
            :is="tableIdsToBeDeleted.includes(performanceTable.id) ? UnTrashIcon : TrashIcon"
            id="trash-icon"
            class="w-4 h-4 cursor-pointer"
            @click="performanceUpdateStore.handleTableDeletion(performanceTable.id)"
          />
          <DocumentDuplicateIcon
            id="clone-icon"
            class="w-4 h-4 cursor-pointer"
            @click="cloneTable(performanceTable.id)"
          />
        </template>
      </PerformanceTableForm>
    </SlotPanel>
  </div>
</template>

<script setup lang="ts">
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, toRefs, watch } from 'vue'
import { useRouter } from 'vue-router'

import DocumentDuplicateIcon from '@/assets/documentDuplicate.svg?component'
import ResArrowLeftIcon from '@/assets/resArrowLeft.svg?component'
import TrashIcon from '@/assets/trash.svg?component'
import UnTrashIcon from '@/assets/unTrash.svg?component'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import PerformanceTableForm from '@/modules/performance/components/organisms/PerformanceTableForm.vue'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import { usePerformanceUpdateStore } from '@/modules/performance/stores/PerformanceUpdateStore'

import { PerformanceTableId, PerformanceTableRead } from '@generated/types'

const props = defineProps<{ tableId: PerformanceTableId | string }>()
const { tableId } = toRefs(props)

const performanceUpdateStore = usePerformanceUpdateStore()
const { updatedPerformance, tableIdsToBeDeleted, tablesWithUpdates } =
  storeToRefs(performanceUpdateStore)

const performanceTable = computed<PerformanceTableRead | undefined>({
  get: () => {
    if (!updatedPerformance.value) return undefined
    return _.find(updatedPerformance.value.tables, { id: tableId.value })
  },
  set: performanceUpdateStore.updateTable,
})
const allowToDelete = computed<boolean>(
  () => _.find(tablesWithUpdates.value, { id: tableId.value })?.allowToDelete,
)

const performanceReservationStore = usePerformanceReservationStore()
const { selectedPerformance } = storeToRefs(performanceReservationStore)
const initialPerformanceTable = computed<PerformanceTableRead | undefined>(() => {
  if (!selectedPerformance.value) return undefined
  return _.find(selectedPerformance.value.tables, { id: tableId.value })
})
onBeforeMount(() => {
  performanceReservationStore.setSelectedTableIds([tableId.value])
})

const router = useRouter()
watch(
  [performanceTable],
  ([perfTable]) => {
    if (!perfTable) router.replace({ name: PERFORMANCE_ROUTE_NAMES.TABLES })
  },
  { immediate: true },
)
function cloneTable(tableId: PerformanceTableId) {
  const newTable = performanceUpdateStore.cloneTable(tableId) as PerformanceTableRead
  router.replace({ params: { tableId: newTable.id } })
  performanceReservationStore.setSelectedTableIds([newTable.id])
}
</script>
